import {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {Button, Modal} from "react-bootstrap";
import Loading from "../../components/loading";
import {Form, Formik, useFormikContext} from "formik";
import {buildingStatRequestSchema} from "../../utils/schemas";
import FormGroup from "../../components/form-group";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {useGlobalNotificationMessage} from "../../context/message";
import {useBuildingStatService} from "../../services/building-stat-service";
import {useBuildingStatTypeService} from "../../services/building-stat-type-service";
import {useBuildingService} from "../../services/building-service";
import {BuildingAccountingPeriodTypes} from "../../utils/constants";

const AddEditBuildingStatModal = forwardRef( ({onSuccess=null, ...props}, ref) => {
  const [show, setShow] = useState(false);
  const [model, setModel] = useState();
  const [loading, setLoading] = useState(false);
  const {addErrorMessage, removeMessage } = useGlobalNotificationMessage();
  const [files, setFiles] = useState([]);
  const [type, setType] = useState(null);
  const [initialBuilding, setInitialBuilding] = useState(null);
  const [accountingPeriodType, setAccountingPeriodType] = useState(null);
  const [buildingStatTypesList, setBuildingStatTypesList] = useState([]);
  const [buildingsList, setBuildingsList] = useState([]);
  const [reset, setReset] = useState(0);

  let formSchema = buildingStatRequestSchema;

  let managerService = useBuildingStatService();
  let buildingStatTypeService = useBuildingStatTypeService();
  let buildingService = useBuildingService();

  const ResolveAccountingPeriodType = () => {
    const { values } = useFormikContext();
    useEffect(() => {
      if (values?.building_id && buildingsList?.length) {
        const b = HelperService.getObjByKey(buildingsList, 'id', parseInt(values.building_id));
        setAccountingPeriodType(b ? b.accounting_period_type : null);
      } else {
        setAccountingPeriodType(null);
      }
    }, [values]);
    return null;
  };

  //expose api for interacting with component
  useImperativeHandle(ref, () => ({
    openModal: openModal,
    closeModal: closeModal,
  }));

  const openModal = (model=null, initialBuildingId='', initialType='') => {
    setLoading(true);
    setFiles([]);

    let promises = [
     Promise.resolve('initOk'),
      buildingStatTypeService.getList(),
      buildingService.getList()
    ];

    if (model && model.id){
      promises.push(getDetail(model.id));
    }

    Promise.all(promises).then(([init=null, buildingStatTypes=[], buildings=[], detail= {}])=>{
      setType(initialType);
      setInitialBuilding(initialBuildingId);

      setBuildingStatTypesList(buildingStatTypes.map((type)=>{
        type.label = type.title + (type.path ? ' | '+type.path : '');
        return type;
      }));
      setBuildingsList(buildings);

      let newDetail = Object.assign({}, {building_id: initialBuildingId, type: initialType, date_at: new Date().toISOString(), stat: ''}, detail);
      setModel(formSchema.cast(newDetail));
      setLoading(false);
    });

    setShow(true);
  }
  const closeModal = () => {
    setShow(false);
    setReset(reset+1);
    setModel(formSchema.cast());
  }

  const getDetail = (id) => {
    return managerService.getDetail(id);
  }

  const onSubmit = (formikValues, { setSubmitting, setErrors }) => {
    removeMessage();
    let reqValues = {...formikValues};
    if (files && files.length){
      reqValues.files = files;
    }

    managerService.addEditModel(reqValues, reqValues.id)
      .then((res) => {
        if (onSuccess && 'function' === typeof onSuccess){
          onSuccess(res);
        }
        setFiles([]);
        closeModal();
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa uložiť zmeny.");
        HelperService.handleFormErrors(error, setErrors);
      })
      .finally(()=>{ setSubmitting(false);});
  };

  return (
    <>
      <Formik
        key={reset}
        enableReinitialize={true}
        initialValues={formSchema.cast(model) || formSchema.cast()}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, handleSubmit, setFieldValue, values}) => (

          <Modal size="lg" show={!!show} onHide={closeModal} centered>
            <Modal.Header closeButton>
              { model && <Modal.Title>{ !model.id ? 'Nový údaj - ' : 'Úprava údaja - ' }{!model?.building_stat_type?.title ? '' : model.building_stat_type.title}</Modal.Title>}
            </Modal.Header>
            <Modal.Body>
              <Section animation={HelperService.getRandomAnimation()}>

                {loading && <Loading className="m-2" />}

                {!loading && <Form className="row">

                  <FormGroup
                    name="type"
                    type="select"
                    disabled={!!type}
                    options={buildingStatTypesList}
                    optionLabel="label"
                    optionKey="id"
                    placeholder="typ"
                    label="Typ údaju"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="building_id"
                    type="select"
                    disabled={!!initialBuilding}
                    options={buildingsList}
                    optionLabel="title"
                    optionKey="id"
                    placeholder="typ"
                    label="Budova"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="stat"
                    placeholder="Hodnota"
                    label="Hodnota"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  {accountingPeriodType===BuildingAccountingPeriodTypes.monthly.val && <FormGroup
                    name="date_at"
                    type="date"
                    placeholder="Ku dňu"
                    label="Ku dňu"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />}

                  {accountingPeriodType===BuildingAccountingPeriodTypes.quarterly.val && <FormGroup
                    name="date_at"
                    type="quarter"
                    placeholder="Ku dňu"
                    label="Ku dňu"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />}

                  <ResolveAccountingPeriodType />

                  <div className="clearfix"></div>

                </Form>}

              </Section>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeModal}>Zatvoriť</Button>
              <Button type="submit" variant="primary" onClick={handleSubmit} disabled={isSubmitting}>Odoslať</Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>

    </>
  );
});

export default AddEditBuildingStatModal;
